import type { QueryClient } from '@tanstack/react-query'
import { drafts as defaultDrafts } from './drafts'

/**
 * Drafts belong to one person. They are wiped on explicit logout (POST /auth/logout answered) and when the
 * signed-in user id differs from the one remembered on this device. A session that merely expired keeps them
 * for the same user; a different user signing in wipes them.
 */

export const LAST_USER_KEY = 'lumora.pwa.uid'

export interface IdentityDeps {
  /** Current user id from the cache: string, null (anonymous) or undefined (not loaded yet). */
  readUserId: () => string | null | undefined
  clearAll: () => Promise<void>
  blockWrites?: (blocked: boolean) => void
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
}

export function createIdentityTracker(deps: IdentityDeps) {
  const { readUserId, clearAll, storage, blockWrites = () => undefined } = deps
  const stored = () => {
    try {
      return storage.getItem(LAST_USER_KEY)
    } catch {
      return null
    }
  }
  return {
    /** The server confirmed the logout (or the session was already gone): wipe everything. */
    async onLogout(): Promise<void> {
      blockWrites(true)
      try {
        storage.removeItem(LAST_USER_KEY)
      } catch {
        /* ignore */
      }
      await clearAll()
    },
    /** Call whenever the `me` cache entry may have changed. */
    async evaluate(): Promise<'cleared' | 'kept'> {
      const id = readUserId()
      if (!id) return 'kept' // unknown or anonymous: an expired session keeps its drafts for the same user
      blockWrites(false)
      const previous = stored()
      try {
        storage.setItem(LAST_USER_KEY, id)
      } catch {
        /* ignore */
      }
      if (previous && previous !== id) {
        await clearAll()
        return 'cleared'
      }
      return 'kept'
    },
  }
}

/** Observes the app's own logout call without touching the API layer. */
export function installLogoutObserver(onLogout: () => void, target: { fetch: typeof fetch } = window): () => void {
  const original = target.fetch
  const wrapped: typeof fetch = async (input, init) => {
    const response = await original.call(target, input, init)
    try {
      const request = input instanceof Request ? input : null
      const method = (init?.method ?? request?.method ?? 'GET').toUpperCase()
      const url = new URL(request ? request.url : String(input instanceof URL ? input.href : input), 'http://local')
      if (method === 'POST' && url.pathname.endsWith('/auth/logout') && (response.ok || response.status === 401)) onLogout()
      // A deleted account is gone for good: wipe its drafts like a logout.
      if (method === 'POST' && url.pathname.endsWith('/me/deletion') && response.ok) onLogout()
    } catch {
      /* never interfere with the request */
    }
    return response
  }
  target.fetch = wrapped
  return () => {
    if (target.fetch === wrapped) target.fetch = original
  }
}

const meUserId = (client: QueryClient): string | null | undefined => {
  const state = client.getQueryState<{ id?: string } | null>(['me'])
  if (!state || state.status !== 'success') return undefined
  return state.data?.id ?? null
}

/** Wires the tracker to the query cache (`['me']`, owned by the API layer) and the logout call. */
export function watchIdentity(client: QueryClient): () => void {
  const tracker = createIdentityTracker({
    readUserId: () => meUserId(client),
    clearAll: () => defaultDrafts.clearAll(),
    blockWrites: (blocked) => defaultDrafts.blockWrites(blocked),
    storage: localStorage,
  })
  const unsubscribeCache = client.getQueryCache().subscribe((event) => {
    const key = event.query.queryKey
    if (key.length === 1 && key[0] === 'me') void tracker.evaluate()
  })
  const stopLogout = installLogoutObserver(() => void tracker.onLogout())
  void tracker.evaluate()
  return () => {
    unsubscribeCache()
    stopLogout()
  }
}
